<script setup lang="ts">
import { computed, ref } from 'vue'
import { getMemoryIcon } from '../assets/design/memory-icons'
import { getTasteIcon } from '../assets/design/taste-icons'
import type { ChoiceOption, Question } from '../types/questionnaire'

const props = defineProps<{
  question: Question
  modelValue: string
  customValue: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:customValue': [value: string]
}>()

const modalVisible = ref(false)
const isTaste = computed(() => props.question.code === 'taste')
const isMemory = computed(() => props.question.code === 'memory_category')
const featuredOptions = computed(() =>
  (props.question.options || []).filter((option) => option.featured),
)
const moreOptions = computed(() =>
  (props.question.options || []).filter((option) => !option.featured),
)
const customSelected = computed(() => props.modelValue === 'custom')

function optionIcon(option: ChoiceOption) {
  if (isTaste.value) {
    return getTasteIcon(option.code)
  }
  if (isMemory.value) {
    return getMemoryIcon(option.code)
  }
  return option.icon
}

function chooseOption(code: string) {
  emit('update:modelValue', props.modelValue === code ? '' : code)
}

function openCustomModal() {
  modalVisible.value = true
}

function closeModal() {
  modalVisible.value = false
}

function updateCustomValue(value: string) {
  emit('update:customValue', value)
  emit('update:modelValue', 'custom')
}

function confirmModal() {
  if (props.modelValue === 'custom' && !props.customValue.trim()) {
    return
  }

  if (!props.modelValue || featuredOptions.value.some((option) => option.code === props.modelValue)) {
    return
  }

  closeModal()
}
</script>

<template>
  <section
    class="question-block question-block--choice"
    :class="{
      'question-block--taste': isTaste,
      'question-block--memory': isMemory,
    }"
  >
    <h2 class="visually-hidden">{{ question.title }}</h2>

    <div class="choice-list">
      <button
        v-for="option in featuredOptions"
        :key="option.code"
        class="choice-card"
        :class="{ 'choice-card--selected': modelValue === option.code }"
        type="button"
        :aria-pressed="modelValue === option.code"
        @click="chooseOption(option.code)"
      >
        <span
          class="choice-visual"
          :class="{ 'choice-visual--placeholder': !optionIcon(option) }"
          :style="{ backgroundColor: option.color || '#e8d5c3' }"
        >
          <img v-if="optionIcon(option)" :src="optionIcon(option)" alt="" />
          <span v-else class="memory-dot" aria-hidden="true" />
          <span class="choice-check" aria-hidden="true">✓</span>
        </span>
        <span class="choice-label">{{ option.label }}</span>
      </button>

      <button
        v-if="question.config?.allowCustom"
        class="choice-card choice-card--custom"
        :class="{ 'choice-card--selected': customSelected }"
        type="button"
        :aria-pressed="customSelected"
        @click="openCustomModal"
      >
        <span class="choice-visual choice-visual--custom">
          <span>＋</span>
          <span class="choice-check" aria-hidden="true">✓</span>
        </span>
        <span class="choice-label">
          {{ customSelected && customValue ? customValue : '自定义' }}
        </span>
      </button>
    </div>

    <div
      v-if="modalVisible"
      class="choice-modal-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="question.config?.customLabel || '自定义答案'"
      @click.self="closeModal"
    >
      <section
        class="choice-modal"
        :class="{ 'choice-modal--compact': moreOptions.length === 0 }"
      >
        <button class="choice-modal-close" type="button" aria-label="关闭" @click="closeModal">
          ×
        </button>

        <div v-if="moreOptions.length > 0" class="choice-modal-grid">
          <button
            v-for="option in moreOptions"
            :key="option.code"
            class="choice-card"
            :class="{ 'choice-card--selected': modelValue === option.code }"
            type="button"
            :aria-pressed="modelValue === option.code"
            @click="chooseOption(option.code)"
          >
            <span
              class="choice-visual"
              :style="{ backgroundColor: option.color || '#e8d5c3' }"
            >
              <img v-if="optionIcon(option)" :src="optionIcon(option)" alt="" />
              <span v-else class="memory-dot" aria-hidden="true" />
              <span class="choice-check" aria-hidden="true">✓</span>
            </span>
            <span class="choice-label">{{ option.label }}</span>
          </button>
        </div>

        <div class="custom-answer">
          <label :for="`${question.code}-custom`">
            {{ question.config?.customLabel || '自定义答案' }}
          </label>
          <input
            :id="`${question.code}-custom`"
            :value="customValue"
            :maxlength="question.config?.customMaxLength"
            :placeholder="isMemory ? '例如：成长' : '请输入您想到的味道……'"
            autocomplete="off"
            @focus="emit('update:modelValue', 'custom')"
            @input="updateCustomValue(($event.target as HTMLInputElement).value)"
          />
        </div>

        <button
          class="modal-confirm-button"
          type="button"
          :disabled="!modelValue || (modelValue === 'custom' && !customValue.trim())"
          @click="confirmModal"
        >
          确认
        </button>
      </section>
    </div>
  </section>
</template>
